import { Capacitor } from '@capacitor/core';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';

/**
 * Sessieopslag voor de native app.
 *
 * Het web houdt zijn sessie in httpOnly cookies. Capacitor serveert de app vanaf een
 * eigen origin (capacitor://localhost op iOS, https://localhost op Android), waardoor
 * elk API-verzoek cross-site is en de webview die cookies (sameSite: 'strict') niet
 * meestuurt. Native bewaart de tokens daarom zelf en zet ze in de Authorization-header,
 * die de backend al accepteert.
 *
 * De versleutelde opslag van het platform en niet @capacitor/preferences: dat laatste is
 * UserDefaults respectievelijk gewone SharedPreferences, en staat onversleuteld in de
 * app-container. De plugin gebruikt de Keychain op iOS en EncryptedSharedPreferences
 * met de Android Keystore op Android.
 *
 * Op het web is alles hier een no-op, zodat api.ts één codepad houdt.
 */

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Markeert het verzoek als native, zodat de backend de tokens in de body teruggeeft.
 *
 * De waarde is het platform zelf ('ios' of 'android') en geen vaste string: de backend
 * kijkt of hij in NATIVE_CLIENT_VALUES staat (backend/src/auth/native-client.ts). Een
 * Android-app die zich als iOS voorstelt zou elk logboek en elk foutrapport onbruikbaar
 * maken. Op het web wordt de header niet verstuurd; getNativeHeaders() geeft daar leeg
 * terug.
 */
export const NATIVE_CLIENT_HEADER = 'X-Client';
export const NATIVE_CLIENT_VALUE = Capacitor.getPlatform();

/**
 * afterFirstUnlockThisDeviceOnly: na een herstart is de sessie er weer zodra het
 * toestel één keer is ontgrendeld, maar de tokens reizen niet mee in een back-up naar
 * een ander toestel.
 *
 * iOS-only begrip; op Android negeert de plugin dit argument en bepaalt de Keystore de
 * levensduur. Het mag daarom onvoorwaardelijk mee.
 */
const KEYCHAIN_ACCESS = KeychainAccess.afterFirstUnlockThisDeviceOnly;

// undefined = nog niet uit de Keychain gelezen, null = gelezen en niet aanwezig.
// Zonder deze cache doet elk API-verzoek een Keychain-lees erbij.
let cachedAccessToken: string | null | undefined;
let cachedRefreshToken: string | null | undefined;

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Een onbereikbare Keychain mag de app niet laten crashen; het betekent simpelweg dat
 * er geen sessie is en de gebruiker opnieuw inlogt.
 */
async function readKey(key: string): Promise<string | null> {
  try {
    const value = await SecureStorage.get(key);
    return typeof value === 'string' ? value : null;
  } catch (error) {
    console.error(`nativeAuth: kon ${key} niet lezen`, error);
    return null;
  }
}

export async function getAccessToken(): Promise<string | null> {
  if (!isNative()) return null;
  if (cachedAccessToken === undefined) {
    cachedAccessToken = await readKey(ACCESS_TOKEN_KEY);
  }
  return cachedAccessToken;
}

export async function getRefreshToken(): Promise<string | null> {
  if (!isNative()) return null;
  if (cachedRefreshToken === undefined) {
    cachedRefreshToken = await readKey(REFRESH_TOKEN_KEY);
  }
  return cachedRefreshToken;
}

export async function setTokens(accessToken: string, refreshToken?: string): Promise<void> {
  if (!isNative()) return;

  try {
    await SecureStorage.set(ACCESS_TOKEN_KEY, accessToken, true, false, KEYCHAIN_ACCESS);
    cachedAccessToken = accessToken;

    // Bij een refresh zonder rotatie stuurt de backend het bestaande token terug; is er
    // niets meegegeven, dan laten we het opgeslagen token met rust.
    if (refreshToken) {
      await SecureStorage.set(REFRESH_TOKEN_KEY, refreshToken, true, false, KEYCHAIN_ACCESS);
      cachedRefreshToken = refreshToken;
    }
  } catch (error) {
    console.error('nativeAuth: kon tokens niet opslaan', error);
    // De cache leegmaken, anders denkt de app dat er een sessie is die een herstart
    // niet overleeft.
    cachedAccessToken = undefined;
    cachedRefreshToken = undefined;
  }
}

export async function clearTokens(): Promise<void> {
  if (!isNative()) return;

  cachedAccessToken = null;
  cachedRefreshToken = null;

  try {
    await SecureStorage.remove(ACCESS_TOKEN_KEY);
    await SecureStorage.remove(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('nativeAuth: kon tokens niet wissen', error);
  }
}

/**
 * De headers die elk native verzoek meekrijgt. Leeg op het web, zodat het cookiepad
 * onveranderd blijft.
 */
export async function getNativeHeaders(): Promise<Record<string, string>> {
  if (!isNative()) return {};

  const token = await getAccessToken();
  return {
    [NATIVE_CLIENT_HEADER]: NATIVE_CLIENT_VALUE,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}
